"use client";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Search,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  FolderOpen,
  Plus,
  Edit3,
  Shield,
  Tag,
} from "lucide-react";
import { useSupportCategories } from "../context/SupportCategoryContext";

const cx = (...c) => c.filter(Boolean).join(" ");

const Pill = ({ children, tone = "gray", size = "md" }) => {
  const toneMap = {
    red: "bg-red-50 text-red-700 ring-red-100",
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    gray: "bg-gray-50 text-gray-700 ring-gray-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    violet: "bg-violet-50 text-violet-700 ring-violet-100",
    yellow: "bg-yellow-50 text-yellow-700 ring-yellow-100",
  };
  const sizeMap = {
    sm: "px-2.5 py-1 text-[11px]",
    md: "px-3 py-1.5 text-xs",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full font-extrabold ring-1 whitespace-nowrap",
        toneMap[tone] || toneMap.gray,
        sizeMap[size] || sizeMap.md
      )}
    >
      {children}
    </span>
  );
};

const SectionDivider = ({ label, right }) => (
  <div className="flex items-center gap-3 my-8">
    {label ? (
      <span className="inline-flex items-center rounded-full bg-white px-4 py-2 text-xs font-extrabold text-gray-800 ring-1 ring-gray-200 shadow-sm">
        {label}
      </span>
    ) : null}
    <div className="h-px flex-1 bg-gradient-to-r from-gray-200 via-gray-200 to-transparent" />
    {right ? <div className="flex items-center gap-2">{right}</div> : null}
  </div>
);

const CategoryModal = ({ isOpen, onClose, onSubmit, category = null, loading }) => {
  const [name, setName] = useState("");
  const [status, setStatus] = useState("Active");

  useEffect(() => {
    if (category) {
      setName(category.name || "");
      setStatus(category.status || "Active");
    } else {
      setName("");
      setStatus("Active");
    }
  }, [category, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    const success = await onSubmit({
      name: name.trim(),
      status: status
    });

    if (success) {
      setName("");
      setStatus("Active");
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-3xl p-8 max-w-md w-full mx-4">
        <h2 className="text-2xl font-extrabold text-gray-900 mb-6">
          {category ? "Edit Category" : "Add New Category"}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-extrabold text-gray-700 mb-2">
              Category Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              placeholder="Enter category name"
              required
              autoFocus
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-extrabold text-gray-700 mb-2">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-3 rounded-2xl ring-1 ring-gray-200 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className="flex-1 px-4 py-3 rounded-2xl bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {loading ? (category ? "Updating..." : "Adding...") : (category ? "Update" : "Add Category")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function SupportCategoriesPage() {
  const {
    supportCategories,
    loading,
    error,
    fetchSupportCategories,
    addSupportCategory,
    updateSupportCategory,
    deleteSupportCategory,
    toggleCategoryStatus,
  } = useSupportCategories();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    fetchSupportCategories();
  }, []);

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return supportCategories;
    return supportCategories.filter((category) =>
      category.name?.toLowerCase().includes(search.toLowerCase())
    );
  }, [supportCategories, search]);

  const paginationData = useMemo(() => {
    const totalPages = Math.ceil(filteredCategories.length / ITEMS_PER_PAGE);
    const start = (page - 1) * ITEMS_PER_PAGE;
    const currentCategories = filteredCategories.slice(start, start + ITEMS_PER_PAGE);
    return { totalPages, currentCategories };
  }, [filteredCategories, page, ITEMS_PER_PAGE]);

  const stats = useMemo(() => ({
    total: filteredCategories.length,
    active: filteredCategories.filter(c => c.status === "Active").length
  }), [filteredCategories]);

  const handleAddCategory = useCallback(async (categoryData) => {
    setActionLoading(true);
    const success = await addSupportCategory(categoryData);
    setActionLoading(false);
    return success;
  }, [addSupportCategory]);

  const handleUpdateCategory = useCallback(async (categoryData) => {
    if (!editingCategory) return false;
    setActionLoading(true);
    const success = await updateSupportCategory(editingCategory.id, categoryData);
    setActionLoading(false);
    return success;
  }, [editingCategory, updateSupportCategory]);

  const handleEditClick = useCallback((category) => {
    setEditingCategory(category);
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingCategory(null);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
    setPage(1);
  }, []);

  const handleQuickStatusToggle = useCallback(async (category) => {
    await toggleCategoryStatus(category.id, category.status);
  }, [toggleCategoryStatus]);

  const handleDelete = useCallback(async (categoryId) => {
    // Simple delete without alert
    await deleteSupportCategory(categoryId);
  }, [deleteSupportCategory]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-[#fff7f6] to-[#f9fafb] flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <Shield className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-xl font-extrabold text-gray-900 mb-2">Error Loading Categories</h2>
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchSupportCategories}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-2xl hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#fff7f6] to-[#f9fafb]">
      <div className="p-6 sm:p-8">
        {/* HERO SECTION */}
        <div className="rounded-3xl bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden mb-8">
          <div className="relative p-7 sm:p-10">
            <div className="pointer-events-none absolute -top-28 -left-28 h-72 w-72 rounded-full bg-blue-200/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 -right-28 h-72 w-72 rounded-full bg-green-200/25 blur-3xl" />

            <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone="blue">
                    <FolderOpen className="h-4 w-4 mr-1" />
                    Support Categories
                  </Pill>
                  <Pill tone="gray">Admin Portal</Pill>
                  <Pill tone="green">
                    <Sparkles className="h-4 w-4 mr-1" />
                    Category Management
                  </Pill>
                </div>

                <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900">
                  Support Categories
                </h1>
                <p className="mt-3 text-gray-600 text-sm sm:text-base">
                  Organize and manage support ticket categories to streamline customer service operations.
                </p>
              </div>

           <div className="flex flex-col w-full sm:flex-row sm:w-auto sm:items-center gap-3">

                <div className="rounded-2xl bg-gray-50 ring-1 ring-gray-200 px-4 py-3">
                  <div className="text-[11px] font-extrabold tracking-widest uppercase text-gray-600">Total</div>
                  <div className="text-sm font-semibold text-gray-900 mt-1">
                    {stats.total} categories
                  </div>
                </div>

                <div className="rounded-2xl bg-green-50 ring-1 ring-green-200 px-4 py-3">
                  <div className="text-[11px] font-extrabold tracking-widest uppercase text-green-600">Active</div>
                  <div className="text-sm font-semibold text-green-900 mt-1">
                    {stats.active} categories
                  </div>
                </div>

                <button
                  onClick={() => setShowModal(true)}
                  className="rounded-2xl bg-red-600 text-white px-4 py-3 hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Category
                </button>
              </div>
            </div>
          </div>
        </div>

        <SectionDivider
          label="Browse Categories"
          right={
            <div className="flex items-center gap-2">
              {search && <Pill tone="amber" size="sm">Search: {search}</Pill>}
              <Pill tone="blue" size="sm">Page {page} of {paginationData.totalPages}</Pill>
            </div>
          }
        />

        {/* SEARCH */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search categories..."
              value={search}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
        </div>

        {/* TABLE */}
        <div className="rounded-3xl bg-white ring-1 ring-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Category
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="3" className="px-6 py-8 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent"></div>
                        <span className="text-sm text-gray-600">Loading categories...</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <>
                    {paginationData.currentCategories.map((category, index) => (
                      <tr
                        key={category.id}
                        className={cx(
                          "hover:bg-gray-50 transition-colors",
                          index !== paginationData.currentCategories.length - 1 ? "border-b border-gray-100" : ""
                        )}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-2xl bg-blue-100 flex items-center justify-center">
                              <Tag className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="font-extrabold text-gray-900">{category.name}</div>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleQuickStatusToggle(category)}
                            className="transition-all duration-200"
                          >
                            <Pill
                              tone={category.status === "Active" ? "green" : "yellow"}
                              size="sm"
                            >
                              {category.status || "Active"}
                            </Pill>
                          </button>
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditClick(category)}
                              className="p-2 rounded-2xl hover:bg-blue-50 text-blue-600 hover:text-blue-700 transition-colors"
                              aria-label="Edit category"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(category.id)}
                              className="p-2 rounded-2xl hover:bg-red-50 text-red-600 hover:text-red-700 transition-colors"
                              aria-label="Delete category"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}

                    {paginationData.currentCategories.length === 0 && (
                      <tr>
                        <td colSpan="3" className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                              <FolderOpen className="h-6 w-6 text-gray-400" />
                            </div>
                            <div>
                              <div className="font-extrabold text-gray-900">No categories found</div>
                              <p className="mt-1 text-sm text-gray-600">
                                {search ? "Try adjusting your search terms." : "Create your first support category."}
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PAGINATION */}
        {paginationData.totalPages > 1 && (
          <div className="flex justify-center items-center gap-3 mt-8">
            <button
              onClick={() => setPage(Math.max(page - 1, 1))}
              disabled={page === 1}
              className={cx(
                "p-3 rounded-2xl ring-1 ring-gray-200 transition-colors",
                page === 1
                  ? "opacity-50 cursor-not-allowed bg-gray-50"
                  : "bg-white hover:bg-gray-50"
              )}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: paginationData.totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={cx(
                    "px-4 py-2 rounded-2xl text-sm font-extrabold transition-colors",
                    pageNum === page
                      ? "bg-red-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50 ring-1 ring-gray-200"
                  )}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            <button
              onClick={() => setPage(Math.min(page + 1, paginationData.totalPages))}
              disabled={page === paginationData.totalPages}
              className={cx(
                "p-3 rounded-2xl ring-1 ring-gray-200 transition-colors",
                page === paginationData.totalPages
                  ? "opacity-50 cursor-not-allowed bg-gray-50"
                  : "bg-white hover:bg-gray-50"
              )}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <CategoryModal
        isOpen={showModal}
        onClose={handleCloseModal}
        onSubmit={editingCategory ? handleUpdateCategory : handleAddCategory}
        category={editingCategory}
        loading={actionLoading}
      />
    </div>
  );
}
